import React, { useEffect, useState } from 'react';
import { Search, Eye, KeyRound, XCircle, PauseCircle, HelpCircle, ChevronRight, Home } from 'lucide-react';
import api from '../../../api';
import BuyerSearchStatusPeopleModal from './BuyerSearchStatusPeopleModal';

/**
 * Buyer search status at the top of Find Buyers (Christian Gravias, DOMA
 * 13374913603, 24 Oct 2026): "Make the info clickable e.g. actively looking &
 * keeping an eye on the market. Please show who the buyers are and then show
 * what there buying preferences are and have a button that says match
 * properties."
 *
 * The four answers a buyer can give on the portal, with Purchased covering
 * both "through Collings" and "elsewhere", then Paused, Recently rented and Not set (buyers with
 * preferences who never answered) so the numbers add up. Same counts as the
 * Analytics board (buyer_lifecycle.status_breakdown).
 */

const NUM = new Intl.NumberFormat('en-AU');

const MAIN = [
  { key: 'active', label: 'Actively looking', statuses: ['active'], Icon: Search },
  { key: 'watching', label: 'Keeping an eye on the market', statuses: ['watching'], Icon: Eye },
  { key: 'purchased', label: 'Purchased', statuses: ['purchased_collings', 'purchased_elsewhere'], Icon: KeyRound },
  { key: 'not_looking', label: 'No longer looking', statuses: ['not_looking'], Icon: XCircle },
];

const MINOR = [
  { key: 'paused', label: 'Paused', statuses: ['paused'], Icon: PauseCircle },
  // Renting for now, back to buying on a return date (CRM-2026-00132).
  { key: 'rented', label: 'Recently rented', statuses: ['rented'], Icon: Home },
  { key: 'not_set', label: 'Not set', statuses: ['not_set'], Icon: HelpCircle },
];

const FindBuyersStatusBoard = () => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('api/buyer-portal/buyer-search-status/breakdown/');
        const breakdown = res.data?.breakdown || {};
        const next = { not_set: breakdown.not_set || 0 };
        (breakdown.statuses || []).forEach((s) => { next[s.value] = s.count; });
        if (!cancelled) setCounts(next);
      } catch (error) {
        console.error('Failed to load buyer search status:', error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const countOf = (tile) => tile.statuses.reduce((n, s) => n + (counts[s] || 0), 0);

  const tile = (t, minor = false) => {
    const count = countOf(t);
    const clickable = count > 0;
    const Tag = clickable ? 'button' : 'div';
    return (
      <Tag
        key={t.key}
        {...(clickable ? { type: 'button', onClick: () => setOpen(t) } : {})}
        className={`w-full text-left rounded-xl border border-gray-100 bg-gray-50/60 ${minor ? 'p-2.5' : 'p-3'} ${
          clickable ? 'cursor-pointer hover:bg-white hover:border-[#55C5D0]/60 hover:shadow-sm transition-all' : ''
        }`}
      >
        <div className="flex items-center gap-2 text-gray-500 mb-1">
          <t.Icon className="w-4 h-4" />
          <span className="text-xs font-medium">{t.label}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`${minor ? 'text-lg' : 'text-2xl'} font-bold text-gray-900`}>
            {loading ? '…' : NUM.format(count)}
          </span>
          {clickable && (
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-[#0e7490]">
              View <ChevronRight className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </Tag>
    );
  };

  const openCount = open ? countOf(open) : 0;

  return (
    <section className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6" aria-label="Buyer search status">
      <h3 className="text-lg font-bold text-gray-900">Buyer search status</h3>
      <p className="text-sm text-gray-500 mb-4">
        Where our buyers are at. Click a number to see who they are, what they want, and the
        properties in our pipeline that match them.
      </p>
      {failed && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          We could not load the buyer search status just now. Refresh the page to try again.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{MAIN.map((t) => tile(t))}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">{MINOR.map((t) => tile(t, true))}</div>

      {open && (
        <BuyerSearchStatusPeopleModal
          isOpen
          withMatches
          onClose={() => setOpen(null)}
          title={open.label}
          subtitle={`${NUM.format(openCount)} ${openCount === 1 ? 'buyer' : 'buyers'}. Most likely to buy first by Buyer Intent Score, or the buyers' own newest answers first.`}
          query={{ status: open.statuses.join(',') }}
          returnTo="/prospecting/find-buyers"
          returnLabel="Find Buyers"
        />
      )}
    </section>
  );
};

export default FindBuyersStatusBoard;
